import { useEffect, useState, useMemo } from 'react';
import { api } from '../../lib/api';
import { useCachedSubmissionsQuery } from '../../hooks/useCachedQuery';
import { useAuthStore } from '../../stores/auth';

export type StatsPeriod = 'last_24h' | 'this_month' | 'last_30_days' | 'all';

export const PERIOD_LABELS: Record<StatsPeriod, string> = {
  last_24h: 'Letzte 24 h',
  this_month: 'Dieser Monat',
  last_30_days: 'Letzte 30 Tage',
  all: 'Gesamt',
};

function getPeriodStart(period: StatsPeriod): Date | null {
  if (period === 'all') return null;
  const now = new Date();
  if (period === 'this_month') {
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }
  if (period === 'last_24h') {
    return new Date(now.getTime() - 24 * 3600 * 1000);
  }
  // last_30_days
  const d = new Date(now);
  d.setDate(d.getDate() - 30);
  return d;
}

export function useDashboardData() {
  const [, setTick] = useState(0);
  const [period, setPeriod] = useState<StatsPeriod>('this_month');

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Admins reichen selbst keine Reports ein — ihre Statistik zeigt das ganze Team,
  // Techniker sehen wie bisher nur die eigenen Einsätze.
  const isAdmin = useAuthStore((s) => s.user?.role) === 'ADMIN';
  const { data: submissionsData } = useCachedSubmissionsQuery(
    ['submissions', isAdmin ? 'team' : 'own'],
    () => api.getSubmissions(isAdmin ? { scope: 'team' } : undefined)
  );

  const allSubmissions = submissionsData?.submissions ?? [];

  const periodStart = useMemo(() => getPeriodStart(period), [period]);

  const filteredSubmissions = useMemo(() => {
    if (!periodStart) return allSubmissions;
    return allSubmissions.filter((s) => new Date(s.createdAt) >= periodStart);
  }, [allSubmissions, periodStart]);

  // WICHTIG: alle Status des Einsatz-Lebenszyklus zählen — die Statistik stand
  // dauerhaft auf 0, weil nur die Legacy-Status (SUBMITTED/CW_PENDING) als
  // „in Bearbeitung" galten; laufende Einsätze sind aber ON_ROUTE/ON_SITE/
  // RETURNING/COMPLETED (lokal fertig, noch nicht übertragen).
  const stats = {
    total: filteredSubmissions.length,
    success: filteredSubmissions.filter((s) => s.status === 'CW_SUCCESS').length,
    pending: filteredSubmissions.filter((s) =>
      ['ON_ROUTE', 'ON_SITE', 'RETURNING', 'COMPLETED', 'SUBMITTED', 'CW_PENDING'].includes(s.status)
    ).length,
    failed: filteredSubmissions.filter((s) => s.status === 'CW_FAILED').length,
    drafts: filteredSubmissions.filter((s) => s.status === 'DRAFT').length,
    parked: filteredSubmissions.filter((s) => ['PARKED', 'PAUSED'].includes(s.status)).length,
  };

  // Parked submissions always from all (not period-filtered) since they're active
  const parkedSubmissions = allSubmissions.filter((s) => ['PARKED', 'PAUSED'].includes(s.status));

  // Recent activity uses all submissions
  const submissions = allSubmissions;

  const nonDraftCount = stats.total - stats.drafts - stats.parked;
  const successRate =
    nonDraftCount <= 0
      ? stats.success > 0 ? 100 : 0
      : Math.min(100, Math.max(0, Math.round((stats.success / nonDraftCount) * 100)));

  return { submissions, stats, parkedSubmissions, successRate, period, setPeriod };
}
